import { Modal, Text } from '@stitch-fix/mode-react';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { CancelReturnModalFooter } from './CancelReturnModalFooter';
import { useReturnInProgressCancelMutation } from '../../utils/hooks/returnInProgressCancel';
import { useToast } from '../../utils/contexts/toast-provider';
import { useBoughtItemsMachine } from '../../utils/machines/boughtItems/context-provider';
import { useBoughtItemsRefetch } from '../BoughtItemsLayout/BoughtItemsRefetchContext';
type CancelReturnModalProps = {
  open: boolean;
  handleModalClose: () => void;
  skuId: string;
  orderId: string;
};

/**
 * Cancel return in progress modal - houses the cancel return in progress flow
 */
export const CancelReturnModal = ({
  open,
  handleModalClose,
  skuId,
  orderId
}: CancelReturnModalProps) => {
  const {
    addToast
  } = useToast();
  const {
    send
  } = useBoughtItemsMachine();
  const {
    refetch
  } = useBoughtItemsRefetch();
  const handleOnModalClose = () => {
    handleModalClose();
  };

  /**
   * Handler for events to be triggered upon the completion of
   * returnInProgressCancel mutation
   */
  const handleReturnInProgressCancelCompleted = async () => {
    send('UPDATE');
    handleModalClose();
    addToast({
      message: 'Your return was successfully canceled.',
      status: 'success'
    });

    // Explicitly refetch and handle the result directly
    try {
      const result = await refetch();
      if (result.data?.client) {
        send('RESOLVE', {
          data: result.data.client
        });
        recordMark('get_bought_items_refetch_completed', {
          graph_query_succeeded: true
        });
      } else {
        send('REJECT', {
          error: 'No data returned after refetch'
        });
        recordMark('get_bought_items_refetch_completed', {
          graph_query_succeeded: false
        });
      }
    } catch (error) {
      // If refetch fails, send REJECT to get out of loading state
      send('REJECT', {
        error: 'Failed to refresh data after canceling return'
      });
      recordMark('get_bought_items_refetch_completed', {
        graph_query_succeeded: false
      });
    }
  };
  const [cancelReturnInProgress] = useReturnInProgressCancelMutation({
    variables: {
      skuId,
      orderId
    },
    onCompleted: () => handleReturnInProgressCancelCompleted(),
    onError: () => {
      addToast({
        message: 'An error occurred. We could not cancel this return. Please try again later.',
        status: 'error'
      });
      handleModalClose();
    }
  });
  return <Modal isOpen={open} heading="Cancel the return?" onClose={() => handleOnModalClose()} footer={<CancelReturnModalFooter handleModalClose={handleOnModalClose} handleOnCancelReturn={cancelReturnInProgress} />}>
      <Text as="p" setting="body-medium-fixed">
        Are you sure you want to cancel the return?
      </Text>
    </Modal>;
};